import React, { useState, useEffect } from 'react';
import { versionsApi } from '../services/api';
import type { TimetableVersion } from '../types';

interface VersionControlProps {
  onVersionLoaded?: () => void;
}

const VersionControl: React.FC<VersionControlProps> = ({ onVersionLoaded }) => {
  const [versions, setVersions] = useState<TimetableVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');

  // Load versions
  const loadVersions = async () => {
    try {
      const response = await versionsApi.getAll();
      setVersions(response.data);
    } catch (error) {
      console.error('Error loading versions:', error);
    }
  };

  // Save current timetable as new version
  const handleSave = async () => {
    if (!saveName.trim()) {
      alert('Please enter a name for this version');
      return;
    }

    setIsLoading(true);
    try {
      // First test if the backend is reachable
      console.log('Testing backend connection...');
      const testResponse = await versionsApi.getAll();
      console.log('Backend connection works, got versions:', testResponse.data);

      // Now try to save
      const saveData = {
        name: saveName.trim(),
        description: saveDescription.trim() || undefined
      };
      
      const repr = (value: any) => `"${value}" (type: ${typeof value}, length: ${value?.length || 'N/A'})`;
      
      console.log('=== FRONTEND DEBUG ===');
      console.log('saveName variable:', repr(saveName));
      console.log('saveDescription variable:', repr(saveDescription));
      console.log('Final save data object:', saveData);
      console.log('JSON.stringify of save data:', JSON.stringify(saveData));
      console.log('Object.keys of save data:', Object.keys(saveData));
      console.log('saveData.name:', repr(saveData.name));
      console.log('saveData.description:', repr(saveData.description));
      
      await versionsApi.save(saveData);
      
      alert(`Version "${saveName}" saved successfully!`);
      setSaveName('');
      setSaveDescription('');
      setShowSaveDialog(false);
      await loadVersions();
    } catch (error: any) {
      console.error('Error saving version:', error);
      alert('Error saving version. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete a version
  const handleDelete = async (version: TimetableVersion) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${version.name}"?\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;

    setIsLoading(true);
    try {
      const response = await versionsApi.delete(version.id);
      if (response.data.success) {
        alert(`Version "${version.name}" deleted successfully`);
        await loadVersions();
      } else {
        alert(`Error: ${response.data.message}`);
      }
    } catch (error: any) {
      console.error('Error deleting version:', error);
      alert('Error deleting version. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load a saved version
  const handleLoad = async (version: TimetableVersion) => {
    const slotCountText = version.slot_count ? `${version.slot_count} slots ` : '';
    const confirmed = window.confirm(
      `Are you sure you want to load "${version.name}"?\n\nThis will replace your current timetable with ${slotCountText}from ${new Date(version.created_at).toLocaleDateString()}.`
    );
    
    if (!confirmed) return;

    setIsLoading(true);
    try {
      const response = await versionsApi.load(version.id);
      if (response.data.success) {
        alert(response.data.message);
        if (onVersionLoaded) {
          onVersionLoaded();
        }
      } else {
        alert(`Error: ${response.data.message}`);
      }
    } catch (error: any) {
      console.error('Error loading version:', error);
      alert('Error loading version. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVersions();
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-montserrat font-semibold text-primary-navy">
          Versions
        </h2>
        <button
          onClick={() => setShowSaveDialog(true)}
          disabled={isLoading}
          className="bg-accent-teal text-white px-3 py-2 text-sm rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50 font-montserrat"
        >
          Save
        </button>
      </div>

      {/* Save Dialog */}
      {showSaveDialog && (
        <div className="mb-6 p-4 border-2 border-accent-teal rounded-lg bg-blue-50">
          <h3 className="font-montserrat font-semibold mb-3">Save Current Timetable</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Version Name *</label>
              <input
                type="text"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g., Draft 1, Final Schedule, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-teal"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description (optional)</label>
              <textarea
                value={saveDescription}
                onChange={(e) => setSaveDescription(e.target.value)}
                placeholder="Any notes about this version..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-teal"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="bg-accent-teal text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save Version'}
              </button>
              <button
                onClick={() => setShowSaveDialog(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Versions List */}
      <div className="space-y-2 max-h-60 overflow-y-auto">
        {versions.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <p className="text-sm">No saved versions yet.</p>
            <p className="text-xs">Save your current timetable to create your first version!</p>
          </div>
        ) : (
          versions.map((version) => (
            <div
              key={version.id}
              className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h4 
                  className="font-montserrat font-semibold text-primary-navy text-sm flex-1" 
                  title={version.name}
                >
                  {version.name}
                </h4>
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleLoad(version)}
                    disabled={isLoading}
                    className="bg-primary-navy text-white w-8 h-8 flex items-center justify-center text-sm rounded hover:bg-opacity-90 transition-colors disabled:opacity-50"
                    title="Load Version"
                  >
                    🔄
                  </button>
                  <button
                    onClick={() => handleDelete(version)}
                    disabled={isLoading}
                    className="bg-red-600 text-white w-8 h-8 flex items-center justify-center text-sm rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                    title="Delete Version"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              {version.description && (
                <p className="text-xs text-gray-600 mb-2 line-clamp-2">{version.description}</p>
              )}
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span>{new Date(version.created_at).toLocaleDateString()}</span>
                {version.slot_count !== undefined && <span>{version.slot_count} slots</span>}
              </div>
            </div>
          ))
        )}
      </div>

      {isLoading && (
        <div className="text-center py-4">
          <div className="inline-flex items-center text-accent-teal">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent-teal mr-2"></div>
            Processing...
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionControl;